import { SearchCarsRequest } from "api"

/**
 * The subset of a cars-search response body this module needs. The generated
 * paginated-list types carry extra fields (pageIndex, pageSize, …); structural
 * typing lets them satisfy this shape.
 */
export interface ClassificationSearchPage<TItem> {
  items: TItem[]
  totalCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

type SearchFn<TItem> = (
  request: SearchCarsRequest,
) => Promise<{ data: { body: ClassificationSearchPage<TItem> | null } }>

function emptyPage<TItem>(): ClassificationSearchPage<TItem> {
  return {
    items: [],
    totalCount: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  }
}

/**
 * Round-robin the per-code result lists so every classification is represented
 * from the first page onwards. Concatenating instead would bury the smaller
 * category (luxury inventory is a fraction of premium) past the first page.
 */
function interleave<TItem>(groups: TItem[][]): TItem[] {
  const longest = Math.max(0, ...groups.map((group) => group.length))
  const merged: TItem[] = []
  for (let index = 0; index < longest; index++) {
    groups.forEach((group) => {
      if (index < group.length) merged.push(group[index])
    })
  }
  return merged
}

/**
 * Runs a cars-search request once per classification code and merges the
 * responses.
 *
 * The API does not union `vehicleSingleValuedClassificationCodes`: asking for
 * both codes returns fewer rows than asking for each on its own. Verified
 * against live sale/new inventory — code 2 alone returns 21 cars and code 3
 * alone returns 2, but [2, 3] returns 21 in total (20 of code 2, 1 of code 3),
 * dropping both code-3 cars found by the single-code query and one code-2 car.
 *
 * Since END-86 merged luxury into פרימיום, that category maps to codes [2, 3],
 * so the merged filter silently lost inventory. This splits the request until
 * the API unions the codes itself; a single-code request is passed straight
 * through untouched.
 *
 * Assumes the caller pages by growing `pageSize` off `requestPage: 0`, which is
 * what every call site does. A merged page cannot be addressed by `requestPage`:
 * page N of the combined list does not line up with page N of either per-code
 * list, so a non-zero `requestPage` would skip the rows trimmed off the previous
 * page. Those rows are reachable only by asking for a larger `pageSize`.
 *
 * A code whose request fails is dropped rather than failing the whole category,
 * so one flaky code cannot empty a category that has inventory. If every code
 * fails the rejection is rethrown, leaving the caller's error handling intact.
 */
export async function searchAcrossClassifications<TItem extends { id: number }>(
  request: SearchCarsRequest,
  search: SearchFn<TItem>,
): Promise<ClassificationSearchPage<TItem>> {
  const codes = request.vehicleSingleValuedClassificationCodes || []

  if (codes.length <= 1) {
    const response = await search(request)
    return response.data?.body || emptyPage()
  }

  const settled = await Promise.allSettled(
    codes.map((code) =>
      search({
        ...request,
        vehicleSingleValuedClassificationCodes: [code],
      }),
    ),
  )

  const pages = settled.flatMap((result) =>
    result.status === "fulfilled" && result.value.data?.body
      ? [result.value.data.body]
      : [],
  )

  if (pages.length === 0) {
    const rejection = settled.find((result) => result.status === "rejected")
    if (rejection) throw (rejection as PromiseRejectedResult).reason
    return emptyPage()
  }

  const seen = new Set<number>()
  const merged = interleave(pages.map((page) => page.items || [])).filter(
    (item) => {
      if (seen.has(item.id)) return false
      seen.add(item.id)
      return true
    },
  )

  // Each request already asked for the full page size, so the merged list can
  // hold up to `codes.length` pages worth of cars. Trim back to what the caller
  // asked for and report the surplus as another page.
  const pageSize = request.pageSize || merged.length
  const items = merged.slice(0, pageSize)

  return {
    items,
    totalCount: pages.reduce((sum, page) => sum + (page.totalCount || 0), 0),
    hasNextPage:
      merged.length > items.length || pages.some((page) => page.hasNextPage),
    hasPreviousPage: pages.some((page) => page.hasPreviousPage),
  }
}

/** The deal types the sale endpoint recognises, in the order they are merged. */
const SALE_CLASSIFICATION_TYPES = ["NewCar", "UsedCar"] as const

/**
 * Runs a sale cars-search request once per `saleCarClassificationType` and
 * merges the responses.
 *
 * Same family of defect as `searchAcrossClassifications` above, on the other
 * filter axis: leaving `saleCarClassificationType` unset does not mean "both",
 * it means "a subset of both". Verified against live sale inventory — NewCar
 * alone returns 49 cars and UsedCar alone returns 65, but an unset request
 * returns 97 of those 114, silently dropping 17 new cars spread across nine
 * brands (END-303).
 *
 * The brand page's "popular cars" section is the first call site converted. It
 * is not the only one affected — the sitemap, the homepage, the root layout and
 * the header search all still send an unset type and lose the same new cars —
 * but those page by `requestPage`, which this cannot serve (see below), so they
 * need their own pass. The search page is genuinely unaffected: it always pins
 * the type from its `dealType` filter.
 *
 * Cars are deduplicated by `vehicleNumber` rather than `id`, which is not unique
 * across the two slices, and interleaved so a section that only renders the
 * first few cars shows a mix of new and used rather than whichever slice
 * happened to be fetched first.
 *
 * A request that already pins `saleCarClassificationType` is passed straight
 * through: the caller has asked for one slice, and fetching both would return
 * cars it means to exclude while spending half its `pageSize` on them.
 *
 * Like its sibling, this assumes the caller pages by growing `pageSize` off
 * `requestPage: 0`. Trimming the merged list back to `pageSize` means page N of
 * the merge does not line up with page N of either slice, so a non-zero
 * `requestPage` would skip the rows trimmed off the previous page. `totalCount`
 * sums the two server counts and is neither deduplicated nor capped at
 * `items.length`, so treat it as an upper bound.
 *
 * A slice whose request fails is dropped rather than failing the whole brand —
 * half a section beats an error page — but the rejection is logged, because
 * silently rendering one slice reproduces the very symptom this fixes and the
 * caller's ISR window would cache it. If both fail the rejection is rethrown,
 * leaving the caller's error handling intact.
 */
export async function searchAcrossSaleClassificationTypes<
  TItem extends { vehicleNumber: number },
>(
  request: SearchCarsRequest,
  search: SearchFn<TItem>,
): Promise<ClassificationSearchPage<TItem>> {
  if (request.saleCarClassificationType) {
    const response = await search(request)
    return response.data?.body || emptyPage()
  }

  const settled = await Promise.allSettled(
    SALE_CLASSIFICATION_TYPES.map((saleCarClassificationType) =>
      search({ ...request, saleCarClassificationType }),
    ),
  )

  const pages = settled.flatMap((result) =>
    result.status === "fulfilled" && result.value.data?.body
      ? [result.value.data.body]
      : [],
  )

  if (pages.length === 0) {
    const rejection = settled.find((result) => result.status === "rejected")
    if (rejection) throw (rejection as PromiseRejectedResult).reason
    return emptyPage()
  }

  settled.forEach((result, index) => {
    if (result.status === "rejected") {
      console.error(
        `[searchAcrossSaleClassificationTypes] ${SALE_CLASSIFICATION_TYPES[index]} slice failed, returning the other slice only`,
        result.reason,
      )
    }
  })

  const seen = new Set<number>()
  const merged = interleave(pages.map((page) => page.items || [])).filter(
    (item) => {
      if (seen.has(item.vehicleNumber)) return false
      seen.add(item.vehicleNumber)
      return true
    },
  )

  const pageSize = request.pageSize || merged.length
  const items = merged.slice(0, pageSize)

  return {
    items,
    totalCount: pages.reduce((sum, page) => sum + (page.totalCount || 0), 0),
    hasNextPage:
      merged.length > items.length || pages.some((page) => page.hasNextPage),
    hasPreviousPage: pages.some((page) => page.hasPreviousPage),
  }
}
